export enum PermissionAreas {
  General = "General",
  OrderManagement = "OrderManagement",
  ReturnsAndClaims = "ReturnsAndClaims",
  Support = "Support",
  Marketing = "Marketing",
  Settings = "Settings",
  Conversion = "Conversion",
  IMS = "IMS",
  /** @deprecated Use IMS instead */
  Inventory = IMS,
  Customers = "Customers",
  Payments = "Payments",
}

// Permission objects with satisfies for type safety
export const GeneralPermissions = {
  MANAGE_TEAM_ROLES: "General_manage-team-roles",
  MANAGE_TEAM_USERS: "General_manage-team-users",
  MANAGE_TEAM_DEVELOPMENT: "General_manage-team-webhooks",
  OTHER: "General_other",
  AUTHENTICATE_WITH_THIRD_PARTIES: "General_authenticate-with-third-parties",
  VIEW_PRODUCTS: "General_view-products",
} as const satisfies Record<string, `General_${string}`>;

export const OrderManagementPermissions = {
  VIEW_ORDER_FULFILLMENT_ANALYTICS:
    "OrderManagement_view-order-fulfillment-analytics",
  VIEW_REPORTS: "OrderManagement_view-reports",
  EDIT_ORDER: "OrderManagement_edit-orders",
  VIEW_ORDER: "OrderManagement_view-orders",
  VIEW_TEAM_USER_WAREHOUSE_PINS:
    "OrderManagement_view-team-user-warehouse-pins",
  MANAGE_SHIPMENTS: "OrderManagement_manage-shipments",
  SCAN_AND_VERIFY: "OrderManagement_scan-and-verify",
  RECEIVE_ORDER_UPDATES: "OrderManagement_receive-order-updates",
  MANAGE_PICK_SESSIONS: "OrderManagement_manage-pick-sessions",
} as const satisfies Record<string, `OrderManagement_${string}`>;

export const ReturnsAndClaimsPermissions = {
  CREATE_RETURN: "ReturnsAndClaims_create-return",
  RESET_RETURN: "ReturnsAndClaims_reset-return",
  APPROVE_RETURN: "ReturnsAndClaims_approve-return",
  PROCESS_RETURN: "ReturnsAndClaims_process-return",
  REOPEN_RETURN: "ReturnsAndClaims_reopen-return",
  FLAG_RETURN: "ReturnsAndClaims_flag-return",
  CLOSE_RETURN: "ReturnsAndClaims_close-return",
  EDIT_RETURN_INFORMATION: "ReturnsAndClaims_edit-return-information",
  RECEIVE_RETURN: "ReturnsAndClaims_receive-return",
  MANAGE_RETURN_SHIPPING: "ReturnsAndClaims_manage-return-shipping",
  MANAGE_RMA: "ReturnsAndClaims_manage-rma",
  RESEND_CUSTOMER_NOTIFICATIONS:
    "ReturnsAndClaims_resend-customer-notifications",
  FILE_CARRIER_CLAIM: "ReturnsAndClaims_file-carrier-claim",
  CHARGE_RECOVERY_FEE: "ReturnsAndClaims_charge-recovery-fee",
  VIEW_RETURN: "ReturnsAndClaims_view-returns",
  MANAGE_PACKAGE_PROTECTION_CLAIM: "ReturnsAndClaims_manage-claims",
  MANAGE_CARRIER_CLAIM: "ReturnsAndClaims_manage-carrier-claims",
  MANAGE_WARRANTY: "ReturnsAndClaims_manage-warranty",
  MANAGE_RECYCLING_REQUESTS: "ReturnsAndClaims_manage-recycle",
  MANAGE_WITHDRAWAL_REQUESTS: "ReturnsAndClaims_manage-withdrawal",
  VIEW_RETURN_AND_CLAIM_ANALYTICS: "ReturnsAndClaims_view-analytics",
} as const satisfies Record<string, `ReturnsAndClaims_${string}`>;

export const SupportPermissions = {
  VIEW_SUPPORT: "Support_view-support",
  VIEW_ANALYTICS: "Support_view-analytics",
  CLOSE_CONVERSATION: "Support_close-tickets",
  ASSIGN_TAG: "Support_add-tags-to-tickets",
  MANAGE_TAG: "Support_create-tags",
  CREATE_REPLY: "Support_reply-to-customers",
  USE_MACRO: "Support_use-templates",
  CREATE_MACRO: "Support_create-templates",
  ARCHIVE_CONVERSATION: "Support_archive-tickets",
  CREATE_TEAM_VIEW: "Support_create-views",
  EDIT_ASSIGNEE: "Support_set-assignee",
  EDIT_CUSTOMER: "Support_set-customer",
  CREATE_RULE: "Support_create-rule",
  DELETE_RULE: "Support_delete-rule",
  VIEW_CUSTOMER_ORDERS: "Support_view-customer-orders",
  VIEW_CUSTOMER_RETURNS: "Support_view-customer-returns",
  MONITOR_AI: "Support_monitor_ai",
  MONITOR_CALLS: "Support_monitor-calls",
} as const satisfies Record<string, `Support_${string}`>;

export const MarketingPermissions = {
  VIEW_MARKETING: "Marketing_view-marketing",
  VIEW_MARKETING_ANALYTICS: "Marketing_view-analytics",
  VIEW_ORDER_TRACKING_ANALYTICS: "Marketing_view-order-tracking-analytics",
  SET_AUDIENCE_COMMUNICATION_STATUS: "Marketing_manage-audience-communication",
  MANAGE_CAMPAIGNS: "Marketing_manage-campaigns",
  MANAGE_TEMPLATES: "Marketing_manage-templates",
  MANAGE_AUTOMATIONS: "Marketing_manage-automations",
  MANAGE_SEGMENTS: "Marketing_manage-segments",
  VIEW_LOYALTY_POINTS: "Marketing_view-loyalty-points",
  EDIT_LOYALTY_POINTS: "Marketing_edit-loyalty-points",
} as const satisfies Record<string, `Marketing_${string}`>;

export const ConversionPermissions = {
  ACCESS_CONVERSION: "Conversion_view-conversion",
  DELETE_POST_PURCHASE_PAGE: "Conversion_delete-post-purchase-page",
} as const satisfies Record<string, `Conversion_${string}`>;

export const ImsPermissions = {
  ACCESS_INVENTORY: "IMS_access-inventory",
  ACCESS_CYCLE_COUNTS: "IMS_access-cycle-counts",
  MANAGE_CYCLE_COUNT_RULES: "IMS_manage-cycle-count-rules",
  ACCESS_FORECASTING: "IMS_access-forecasting",
  ACCESS_SCENARIO_PLANNING: "IMS_access-scenario-planning",
  ACCESS_SUPPLIERS: "IMS_access-suppliers",
  ACCESS_BILL_OF_MATERIALS: "IMS_access-bill-of-materials",
  ACCESS_PURCHASE_ORDERS: "IMS_access-purchase-orders",
  ACCESS_SHIPMENTS: "IMS_access-shipments",
  ACCESS_TRANSFERS: "IMS_access-transfers",
  VIEW_REPORTS: "IMS_access-reporting",
  ACCESS_INTELLIGENT_INVENTORY: "IMS_access-intelligent-inventory",
} as const satisfies Record<string, `IMS_${string}`>;

/** @deprecated Use ImsPermissions instead */
export const InventoryPermissions = ImsPermissions;

export const CustomersPermissions = {
  VIEW_CUSTOMER_RECORD: "Customers_view-customer-record",
} as const satisfies Record<string, `Customers_${string}`>;

export const PaymentsPermissions = {
  VIEW_HSA_PAYMENTS: "Payments_view-hsa-payments",
} as const satisfies Record<string, `Payments_${string}`>;

/**
 * Hello, reader! You are probably wondering why this isn't just baked into existing product based
 * permission enums.
 *
 * The answer is that some categories of settings aren't always good at fitting into specific product categories ATM (but this may change)
 *
 * Please put all settings related permissions here, even if they are related to a specific product.
 */
export const SettingsPermissions = {
  SET_GENERAL_SETTINGS: "Settings_set-general-settings",
  SET_AI_SETTINGS: "Settings_set-ai-settings",
  SET_BILLING_SETTINGS: "Settings_set-billing-settings",
  VIEW_BILLING: "Settings_view-billing",
  SET_CHECKOUT_OPTIMIZATION_SETTINGS:
    "Settings_set-checkout-optimization-settings",
  SET_CUSTOMER_ACCOUNTS_SETTINGS: "Settings_set-customer-accounts-settings",
  SET_MARKETING_SETTINGS: "Settings_set-marketing-settings",
  SET_RETURNS_AND_CLAIMS_SETTINGS: "Settings_set-returns-and-claims-settings",
  SET_SHOPIFY_RETURN_FEE_SYNC_SETTINGS:
    "Settings_set-shopify-return-fee-sync-settings",
  SET_ORDER_FULFILLMENT_SETTINGS: "Settings_set-order-fulfillment-settings",
  SET_SHIPPING_SETTINGS: "Settings_set-shipping-settings",
  SET_SUPPORT_SETTINGS: "Settings_set-support-settings",
  SET_TNPL_SETTINGS: "Settings_set-tnpl-settings",
  SET_WARRANTY_SETTINGS: "Settings_set-warranty-settings",
} as const satisfies Record<string, `Settings_${string}`>;

// Derive array for Zod from the objects
export const userPermissions = [
  ...Object.values(GeneralPermissions),
  ...Object.values(OrderManagementPermissions),
  ...Object.values(ReturnsAndClaimsPermissions),
  ...Object.values(SupportPermissions),
  ...Object.values(MarketingPermissions),
  ...Object.values(SettingsPermissions),
  ...Object.values(InventoryPermissions),
  ...Object.values(ConversionPermissions),
  ...Object.values(CustomersPermissions),
  ...Object.values(PaymentsPermissions),
] as const;

// Derive TypeScript types
export type GeneralPermissionsType =
  (typeof GeneralPermissions)[keyof typeof GeneralPermissions];

export type OrderManagementPermissionsType =
  (typeof OrderManagementPermissions)[keyof typeof OrderManagementPermissions];

export type ReturnsAndClaimsPermissionsType =
  (typeof ReturnsAndClaimsPermissions)[keyof typeof ReturnsAndClaimsPermissions];

export type SupportPermissionsType =
  (typeof SupportPermissions)[keyof typeof SupportPermissions];

export type MarketingPermissionsType =
  (typeof MarketingPermissions)[keyof typeof MarketingPermissions];

export type SettingsPermissionsType =
  (typeof SettingsPermissions)[keyof typeof SettingsPermissions];

export type ConversionPermissionsType =
  (typeof ConversionPermissions)[keyof typeof ConversionPermissions];

export type ImsPermissionsType =
  (typeof ImsPermissions)[keyof typeof ImsPermissions];

/** @deprecated Use ImsPermissionsType instead */
export type InventoryPermissionsType = ImsPermissionsType;

export type CustomerPermissionsType =
  (typeof CustomersPermissions)[keyof typeof CustomersPermissions];

export type PaymentsPermissionsType =
  (typeof PaymentsPermissions)[keyof typeof PaymentsPermissions];

export type UserPermissions =
  | GeneralPermissionsType
  | OrderManagementPermissionsType
  | ReturnsAndClaimsPermissionsType
  | SupportPermissionsType
  | MarketingPermissionsType
  | SettingsPermissionsType
  | ConversionPermissionsType
  | InventoryPermissionsType
  | CustomerPermissionsType
  | PaymentsPermissionsType;
